import React from 'react';
import { SearchBreadcrumb } from '../../../../../../components/molecules/SearchBreadcrumb';
import { ContentParagraph, ContentParagraphModifier } from '../../../../ContentParagraph';
import Btn from '../../../../../../components/molecules/Btn';
import { ContentHeadline } from '../../../../ContentHeadline';
import { Icons } from '../../../../../../components/atoms/SVG';
import { getHexColor } from '../../../../../../types/ICategory';
import { IConnectedPage } from '../../../../../../types/api/connectedPages/IConnectedPage';
import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import { ConnectedPageListItemPersons } from '../ConnectedPageListItemPersons/ConnectedPageListItemPersons';
import { PageConnectionDetails } from '../../../../PageConnectionDetails/PageConnectionDetails';
import { LinkedTeaserImage } from '../../../../../../components/molecules/LinkedTeaserImageInLists';
import { useHtmlValidId } from '../../../../../../hooks/useHtmlValidId';
import { ConnectedPagesModeSearchPaths } from '../../../../../../types/api/connectedPages/ConnectedPagesMode';
import { TooltipPlacement } from '../../../../../../components/atoms/Tooltip';

type ConnectedPagesListItemProps = {
  page: IConnectedPage;
  depth?: number;
  isActive(page: IConnectedPage): boolean;
  onClick(page: IConnectedPage): void;
};

export function ConnectedPageListItem({ page, depth = 0, onClick, isActive }: ConnectedPagesListItemProps) {
  const { t } = useSonTranslation('ConnectedPageListItem');
  const {
    pageLink,
    description = '',
    path = [],
    categories = [],
    contactPersons = [],
    otherPersons = [],
    childPages = [],
    roleNames,
    pageType,
  } = page;
  const active = isActive(page);
  const hasChildren = childPages.length > 0;
  const expandButtonText = t(active ? 'closeFurtherLevels' : 'openFurtherLevels', {
    depth: depth ? depth + 2 : 2,
  });
  const hasContactPersons = contactPersons.length > 0;
  const hasOtherPersons = otherPersons.length > 0;

  const uId = useHtmlValidId();

  return (
    <li className="connected-page-list-item" key={pageLink.url}>
      <div className="connected-page-list-item__container" style={{ borderLeftColor: getHexColor(categories) }}>
        {page.teaserImage && (
          <div className="connected-page-list-item__image-container">
            <LinkedTeaserImage imageSrc={page.teaserImage.src} pageLink={page.pageLink.url} />
          </div>
        )}
        <article className="connected-page-list-item__article">
          <div className="connected-page-list-item__connected-page-container">
            <div className="connected-page-list-item__connected-page">
              <ContentHeadline
                url={`${pageLink.url}${hasChildren ? `?${ConnectedPagesModeSearchPaths.tree}` : ''}`}
                title={pageLink.name}
                headingId={`connected-page-item-headline-${uId}`}
                modifier="top-headline"
              />
              <PageConnectionDetails
                modifier="padding-bottom"
                roles={roleNames}
                pageType={pageType}
                categories={categories}
              />
              <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>{description}</ContentParagraph>
              <SearchBreadcrumb breadcrumbs={path} />
            </div>
            {hasChildren && (
              <span className="connected-page-list-item__icon-container">
                <Btn
                  onClick={() => onClick(page)}
                  type="button"
                  cssClass="btn--minor-action btn--transparent"
                  svgSpriteId={active ? Icons.ARROW_UP : Icons.ARROW_DOWN}
                  ariaControls={`${pageLink.url}-content`}
                  ariaExpanded={active}
                  hiddenText
                  text={expandButtonText}
                  titleText={expandButtonText}
                  tooltipPlacement={TooltipPlacement.TOP}
                />
              </span>
            )}
          </div>
          {(hasContactPersons || hasOtherPersons) && (
            <div className="connected-page-list-item__person-container">
              {hasContactPersons && (
                <ConnectedPageListItemPersons persons={contactPersons} headline={t('headlineContactPersons')} />
              )}
              {hasOtherPersons && (
                <ConnectedPageListItemPersons persons={otherPersons} headline={t('headlineAllPersons')} />
              )}
            </div>
          )}
        </article>
      </div>
      {childPages.length > 0 && (
        <div id={`${pageLink.url}-content`} className="connected-page-list-item__content" aria-hidden={!active}>
          {active && (
            <ul>
              {childPages.map(childPage => (
                <ConnectedPageListItem
                  key={`pageLink-${childPage.pageLink.url}`}
                  page={childPage}
                  isActive={isActive}
                  onClick={onClick}
                  depth={depth + 1}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
